/**
 * Swamithra — the shared enumerations, and the ONLY place a wire value is turned
 * into something a person reads.
 *
 * The wire values are lowercase snake_case (API_STYLE_GUIDE.md §4); the labels
 * are what appears in the user app and the Expert panel. Keeping the mapping
 * here is what makes US-01-001.13 / US-01-004.21 true by construction: there is
 * no second place that could render `in_progress` as "In progress", and the word
 * "Reviewed" does not exist anywhere.
 */

import { z } from "zod"

// -----------------------------------------------------------------------------
// Work status — shared by queries AND vetting requests
// -----------------------------------------------------------------------------

/**
 * One enum for both entities, because the acceptance criteria specify one
 * ("the shared enum", US-01-006.4, US-01-004.21). Two copies would drift the
 * moment somebody adds a fifth state to one of them.
 */
export const workStatusSchema = z.enum(["open", "in_progress", "answered", "closed"])
export type WorkStatus = z.infer<typeof workStatusSchema>

export const WORK_STATUS_LABELS: Record<WorkStatus, string> = {
	open: "Open",
	in_progress: "In Progress",
	answered: "Answered",
	closed: "Closed",
}

/** Display order for status filters and the status-split card. */
export const WORK_STATUSES: readonly WorkStatus[] = workStatusSchema.options

export function workStatusLabel(status: WorkStatus): string {
	return WORK_STATUS_LABELS[status]
}

/**
 * True when the thread still accepts a follow-up message.
 *
 * Closed hides the composer and a POST returns 409 (US-00-002.12,
 * US-01-003.9).
 */
export function acceptsFollowUp(status: WorkStatus): boolean {
	return status !== "closed"
}

// -----------------------------------------------------------------------------
// Close reason
// -----------------------------------------------------------------------------

/**
 * Why a query left the Answered/In Progress state.
 *
 * NEVER present on the user-facing payload (US-01-003.13) — the user-side query
 * schemas in ./query deliberately omit it, and that omission is the enforcement.
 */
export const closeReasonSchema = z.enum(["expert_close", "user_feedback", "inactivity"])
export type CloseReason = z.infer<typeof closeReasonSchema>

export const CLOSE_REASON_LABELS: Record<CloseReason, string> = {
	expert_close: "Closed by expert",
	user_feedback: "Closed on user feedback",
	inactivity: "Closed after 7 days of inactivity",
}

// -----------------------------------------------------------------------------
// User type
// -----------------------------------------------------------------------------

/**
 * THE WIRE VALUE IS SINGULAR — `professional`, not `professionals`.
 *
 * That is what the service's taxonomy defines, what its `user_type` column
 * stores and what `registered_users.professional` is keyed by in the reporting
 * summary (openapi.yaml `User.user_type`). The PLURAL is the label a person
 * reads, which is why the two are separated below rather than being the same
 * string used twice.
 */
export const userTypeSchema = z.enum(["individual", "professional"])
export type UserType = z.infer<typeof userTypeSchema>

export const USER_TYPE_LABELS: Record<UserType, string> = {
	individual: "Individual",
	professional: "Professionals",
}

/**
 * Order of the registration radio group and the reporting split.
 *
 * Stated rather than taken from `.options`, because the criteria name the order
 * — "User Type (Professionals/Individual)" (US-00-001.1), "Professionals vs
 * Individual" (US-01-006.11) — and the enum's own order is alphabetical.
 */
export const USER_TYPES: readonly UserType[] = ["professional", "individual"]

// -----------------------------------------------------------------------------
// Feedback
// -----------------------------------------------------------------------------

export const feedbackRatingSchema = z.enum(["satisfied", "not_satisfied"])
export type FeedbackRating = z.infer<typeof feedbackRatingSchema>

export const FEEDBACK_RATING_LABELS: Record<FeedbackRating, string> = {
	satisfied: "Satisfied",
	not_satisfied: "Not Satisfied",
}

// -----------------------------------------------------------------------------
// Message authorship
// -----------------------------------------------------------------------------

/**
 * Who wrote a message in a thread.
 *
 * Drives bubble alignment on mobile — user right, expert left (US-00-002.31) —
 * so it is part of the contract rather than something inferred from whether an
 * id matches the signed-in user.
 */
export const senderTypeSchema = z.enum(["user", "expert"])
export type SenderType = z.infer<typeof senderTypeSchema>

// -----------------------------------------------------------------------------
// Panel roles
// -----------------------------------------------------------------------------

/**
 * `super_admin` is provisioned once by a deployment task and is not settable
 * through any screen or endpoint (US-01-005.22). The value exists here so the
 * panel can hide expert-management from a plain expert; it is never sent.
 */
export const expertRoleSchema = z.enum(["expert", "super_admin"])
export type ExpertRole = z.infer<typeof expertRoleSchema>

export const EXPERT_ROLE_LABELS: Record<ExpertRole, string> = {
	expert: "Expert",
	super_admin: "Super Admin",
}
